import { useCallback, useEffect, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { ScanInput, type ScanInputProps } from 'shared/components/scan-input'
import { Button } from 'shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/components/ui/card'
import { DamageAct } from 'shared/components/damage-act'
import { useTranslate } from 'shared/lib/i18n'
import { PointDayMark } from 'shared/components/point-day'
import { cn } from 'shared/lib/utils'
import { TONE_CLASS } from 'shared/components/tone-banner'
import {
	CashBagTakeBanner,
	StopCashBags,
	type DriverCashBagStops,
	type DriverTakeAnswer,
	type DriverTakeRequest,
} from './DriverCashBags'
import { cashBagsViaTrpc, takeViaTrpc } from './driver-cash-bags-api'

/**
 * The driver's phone (spec §13, §26 view B; ADR-0024; ticket 27): my Trips, then one
 * Trip's checklist per Point, and the loading scan. The scan is the phone's camera,
 * a Bluetooth scanner or the keyboard — the one scan field; the answer comes back
 * in its colour —
 * loaded green, a repeat yellow, «не ваш рейс» and «мешок не закрыт» red. The colour
 * is the server's; this screen only shows it.
 *
 * The return flow (ADR-0037, spec §13; ticket 42): each Point's checklist also lists
 * what to collect there — the system's pickup list — and the same scan field takes a
 * Parcel off it: a code that is not a Bag goes to the pickup, at the Points of this
 * Trip only («забрал» green, a Parcel not on the list red). A collected Parcel may be
 * recorded damaged here, with a comment.
 *
 * The Cash bag (tickets 31, 57, `DriverCashBags.tsx`): each Point's checklist also
 * names the bag packed there — one waits per Point, with nothing on it to scan
 * (ADR-0047) — and «Забрать сумку пункта N» takes it, without counting. The scan
 * field is for Bags and Parcels only: a code that is not a Bag is a Parcel to
 * collect.
 *
 * The calls are injected (`listTrips`, `loadTrip`, `load`, `collect`, `loadCashBags`,
 * `take`) so the
 * behaviour is tested without a backend; they default to the real tRPC client.
 */

export type DriverTripRow = RouterOutputs['trips']['List'][number]
export type DriverTrip = RouterOutputs['trips']['Trip']
export type DriverLoadAnswer = RouterOutputs['trips']['Load']
export type DriverLoadRequest = RouterInputs['trips']['Load']
export type DriverCollectAnswer = RouterOutputs['trips']['Collect']
export type DriverCollectRequest = RouterInputs['trips']['Collect']
type ScanAnswer = DriverLoadAnswer | DriverCollectAnswer

const listViaTrpc = () => trpcClient.trips.List.query()
const tripViaTrpc = (tripId: string) => trpcClient.trips.Trip.query({ tripId })
const loadViaTrpc = (input: DriverLoadRequest) => trpcClient.trips.Load.mutate(input)
const collectViaTrpc = (input: DriverCollectRequest) => trpcClient.trips.Collect.mutate(input)

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

export interface DriverProps {
	listTrips?: () => Promise<DriverTripRow[]>
	loadTrip?: (tripId: string) => Promise<DriverTrip>
	load?: (input: DriverLoadRequest) => Promise<DriverLoadAnswer>
	/** Test seam: the pickup scan of the return flow. */
	collect?: (input: DriverCollectRequest) => Promise<DriverCollectAnswer>
	/** Test seam: the Trip's Cash bags per Point, and taking one (ticket 31). */
	loadCashBags?: (tripId: string) => Promise<DriverCashBagStops>
	take?: (input: DriverTakeRequest) => Promise<DriverTakeAnswer>
	/** Test seam: the camera of the scan field (a fake drives a camera read). */
	renderCamera?: ScanInputProps['renderCamera']
}

export function Driver({
	listTrips = listViaTrpc,
	loadTrip = tripViaTrpc,
	load = loadViaTrpc,
	collect = collectViaTrpc,
	loadCashBags = cashBagsViaTrpc,
	take = takeViaTrpc,
	renderCamera,
}: DriverProps) {
	const { t } = useTranslate()
	const [trips, setTrips] = useState<DriverTripRow[] | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [tripId, setTripId] = useState<string | null>(null)

	useEffect(() => {
		listTrips().then(setTrips, (e) => setError(failure(e)))
	}, [listTrips])

	if (tripId) {
		return (
			<DriverTripView
				tripId={tripId}
				loadTrip={loadTrip}
				load={load}
				collect={collect}
				loadCashBags={loadCashBags}
				take={take}
				renderCamera={renderCamera}
				onBack={() => setTripId(null)}
			/>
		)
	}

	return (
		<div className='flex flex-col gap-3' data-testid='driver-trips'>
			<h1 className='text-xl font-semibold'>{t('driver.listTitle')}</h1>
			{error ? (
				<p className='text-sm text-destructive' data-testid='driver-error'>
					{t('driver.loadFailed', { message: error })}
				</p>
			) : trips === null ? (
				<p className='text-sm text-muted-foreground'>{t('driver.loading')}</p>
			) : trips.length === 0 ? (
				<p className='text-sm text-muted-foreground' data-testid='driver-empty'>
					{t('driver.empty')}
				</p>
			) : (
				trips.map((trip) => (
					<Card key={trip.tripId} data-testid={`driver-trip-${trip.code}`}>
						<CardContent className='flex flex-col gap-2'>
							<div className='flex items-center gap-2'>
								<span className='font-mono text-lg font-semibold'>{trip.code}</span>
								<span className='text-sm text-muted-foreground'>
									{t('driver.stops', { count: String(trip.stops.length) })}
								</span>
							</div>
							<div className='text-sm'>{trip.stops.map((s) => s.pointCode).join(' → ')}</div>
							<div className='text-sm text-muted-foreground'>
								{t('driver.counts', {
									waiting: String(trip.counts.waiting),
									loaded: String(trip.counts.loaded),
									delivered: String(trip.counts.delivered),
								})}
							</div>
							<Button
								data-testid={`driver-open-${trip.code}`}
								onClick={() => setTripId(trip.tripId)}
							>
								{t('driver.open')}
							</Button>
						</CardContent>
					</Card>
				))
			)}
		</div>
	)
}

function DriverTripView({
	tripId,
	loadTrip,
	load,
	collect,
	loadCashBags,
	take,
	renderCamera,
	onBack,
}: {
	tripId: string
	loadTrip: (tripId: string) => Promise<DriverTrip>
	load: (input: DriverLoadRequest) => Promise<DriverLoadAnswer>
	collect: (input: DriverCollectRequest) => Promise<DriverCollectAnswer>
	loadCashBags: (tripId: string) => Promise<DriverCashBagStops>
	take: (input: DriverTakeRequest) => Promise<DriverTakeAnswer>
	renderCamera?: ScanInputProps['renderCamera']
	onBack: () => void
}) {
	const { t } = useTranslate()
	const [trip, setTrip] = useState<DriverTrip | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<ScanAnswer | null>(null)
	const [cashBags, setCashBags] = useState<DriverCashBagStops>([])
	const [cashAnswer, setCashAnswer] = useState<DriverTakeAnswer | null>(null)
	const [damaging, setDamaging] = useState<string | null>(null)
	const [sendError, setSendError] = useState<string | null>(null)
	const [sending, setSending] = useState(false)
	// Bumped after each answer so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)
	const reads = useRef(0)

	// The checklist is the server's: re-read after every scan rather than rebuilt here.
	const refresh = useCallback(() => {
		const read = ++reads.current
		loadTrip(tripId).then(
			(next) => {
				if (read !== reads.current) return
				setTrip(next)
				setError(null)
			},
			(e) => {
				if (read === reads.current) setError(failure(e))
			},
		)
		// The Cash bags beside the checklist; without them the rest still shows.
		loadCashBags(tripId).then(
			(next) => {
				if (read === reads.current) setCashBags(next)
			},
			() => {},
		)
	}, [loadTrip, loadCashBags, tripId])

	useEffect(() => {
		refresh()
	}, [refresh])

	const onScan = async (code: string) => {
		if (sending) return
		setSending(true)
		setSendError(null)
		try {
			// A Bag or a whole Box is loaded; any other code is a Parcel of the return flow to collect.
			const loaded = await load({ tripId, code })
			const notAContainer = loaded.outcome === 'refused' && loaded.reason === 'not_a_container'
			setCashAnswer(null)
			setAnswer(notAContainer ? await collect({ tripId, code }) : loaded)
			setScanRound((n) => n + 1)
			refresh()
		} catch (e) {
			setSendError(failure(e))
		} finally {
			setSending(false)
		}
	}

	/** «Забрать сумку пункта N»: the one bag waiting there, chosen — not scanned. */
	const takeBag = async (cashBagId: string) => {
		if (sending) return
		setSending(true)
		setSendError(null)
		try {
			setAnswer(null)
			setCashAnswer(await take({ tripId, cashBagId }))
			refresh()
		} catch (e) {
			setSendError(failure(e))
		} finally {
			setSending(false)
		}
	}

	return (
		<div className='flex flex-col gap-3' data-testid='driver-trip'>
			<Button
				variant='ghost'
				size='sm'
				className='w-fit'
				data-testid='driver-back'
				onClick={onBack}
			>
				{t('driver.back')}
			</Button>
			{error ? (
				<p className='text-sm text-destructive' data-testid='driver-error'>
					{t('driver.loadFailed', { message: error })}
				</p>
			) : trip === null ? (
				<p className='text-sm text-muted-foreground'>{t('driver.loading')}</p>
			) : (
				<>
					<div>
						<h1 className='font-mono text-xl font-semibold'>{trip.code}</h1>
						<div className='text-sm text-muted-foreground' data-testid='driver-counts'>
							{t('driver.counts', {
								waiting: String(trip.counts.waiting),
								loaded: String(trip.counts.loaded),
								delivered: String(trip.counts.delivered),
							})}
						</div>
					</div>

					<Card>
						<CardContent className='flex flex-col gap-3'>
							<p className='text-sm text-muted-foreground'>{t('driver.scanHint')}</p>
							<ScanInput
								key={scanRound}
								onScan={onScan}
								disabled={sending}
								renderCamera={renderCamera}
							/>
							{sendError && (
								<p
									role='alert'
									className='text-sm text-destructive'
									data-testid='driver-scan-error'
								>
									{t('driver.scanFailed', { message: sendError })}
								</p>
							)}
							{answer && <LoadAnswerBanner answer={answer} />}
							{cashAnswer && <CashBagTakeBanner answer={cashAnswer} />}
						</CardContent>
					</Card>

					{trip.stops.map((stop, i) => (
						<Card key={stop.pointId} data-testid={`driver-stop-${stop.pointId}`}>
							<CardHeader>
								<CardTitle>
									{i + 1}. {stop.pointCode}
								</CardTitle>
								<PointDayMark day={stop} testId={`driver-stop-day-${stop.pointId}`} />
							</CardHeader>
							<CardContent className='flex flex-col gap-1.5'>
								{stop.containers.length === 0 ? (
									<p className='text-sm text-muted-foreground'>{t('driver.noBags')}</p>
								) : (
									stop.containers.map((bag) => (
										<div
											key={bag.containerId}
											className='flex flex-wrap items-center gap-2 rounded-md border px-3 py-2'
											data-testid={`driver-bag-${bag.code}`}
											data-stage={bag.stage}
											data-kind={bag.kind}
										>
											<span className='text-sm text-muted-foreground'>{t(`ridingKind.${bag.kind}`)}</span>
											<span className='font-mono'>{bag.code}</span>
											<span className='text-sm text-muted-foreground'>
												{t('driver.bagParcels', {
													count: String(bag.parcelCount),
												})}
											</span>
											<span className='ml-auto rounded px-1.5 text-xs font-semibold ring-1 ring-current'>
												{t(`tripStage.${bag.stage}`)}
											</span>
										</div>
									))
								)}
								{stop.collect.length > 0 && (
									<div className='mt-2 flex flex-col gap-1.5' data-testid={`driver-collect-${stop.pointId}`}>
										<div className='text-sm font-medium'>{t('driver.collectTitle')}</div>
										{stop.collect.map((row) => (
											<div key={row.parcelId} className='flex flex-col gap-2'>
												<div
													className='flex flex-wrap items-center gap-2 rounded-md border px-3 py-2'
													data-testid={`driver-pickup-${row.parcelId}`}
													data-collected={row.collected}
												>
													<span className='font-mono'>{row.trackingCode}</span>
													<span className='text-sm text-muted-foreground'>
														{t(`pickupReason.${row.reason}`)}
													</span>
													<span className='ml-auto rounded px-1.5 text-xs font-semibold ring-1 ring-current'>
														{row.collected ? t('driver.collected') : t('driver.toCollect')}
													</span>
													{row.collected && (
														<Button
															size='sm'
															variant='outline'
															data-testid={`driver-damage-${row.parcelId}`}
															onClick={() => setDamaging(row.parcelId)}
														>
															{t('damage.open')}
														</Button>
													)}
												</div>
												{damaging === row.parcelId && (
													<DamageAct
														parcelId={row.parcelId}
														code={row.trackingCode}
														onCancel={() => setDamaging(null)}
													/>
												)}
											</div>
										))}
									</div>
								)}
								<StopCashBags
									pointCode={stop.pointCode}
									bags={cashBags.find((c) => c.pointId === stop.pointId)?.bags ?? []}
									busy={sending}
									onTake={takeBag}
								/>
							</CardContent>
						</Card>
					))}
				</>
			)}
		</div>
	)
}

/** The refusals of a pickup scan — said as «не забрал», not «не погружен». */
const PICKUP_REFUSALS = ['unknown_code', 'ambiguous_code', 'not_on_this_trip', 'not_for_pickup'] as const

/** The answer to the last scan: large, in its colour, readable at a glance. */
function LoadAnswerBanner({ answer }: { answer: ScanAnswer }) {
	const { t } = useTranslate()
	let title: string
	let detail: string
	switch (answer.outcome) {
		case 'loaded':
			title = t(answer.container.kind === 'partner_box' ? 'driver.answerLoadedBox' : 'driver.answerLoaded', { point: answer.pointCode })
			detail = t('driver.answerLoadedDetail', {
				kind: t(`ridingKind.${answer.container.kind}`),
				code: answer.container.code,
			})
			break
		case 'already_loaded':
			title = t('driver.answerAlready')
			detail = t('driver.answerAlreadyDetail', {
				kind: t(`ridingKind.${answer.container.kind}`),
				code: answer.container.code,
				point: answer.pointCode,
			})
			break
		case 'collected':
			title = t('driver.answerCollected', { point: answer.pointCode })
			detail = t('driver.answerCollectedDetail', {
				code: answer.trackingCode,
				reason: t(`pickupReason.${answer.reason}`),
			})
			break
		case 'already_collected':
			title = t('driver.answerAlreadyCollected')
			detail = answer.trackingCode
			break
		case 'found':
			title = t('driver.answerFound')
			detail = t('driver.answerFoundDetail', { code: answer.trackingCode, point: answer.pointCode })
			break
		case 'refused': {
			// A loading and a pickup refusal speak from one list of words.
			const reason = t(`tripRefusal.${answer.reason}`) || answer.reason
			// A loading's refusal carries the Trip's counts; a pickup's does not.
			const parcel = !('counts' in answer) && (PICKUP_REFUSALS as readonly string[]).includes(answer.reason)
			title = parcel ? t('driver.answerNotCollected') : t('driver.answerRefused')
			detail = answer.pointCode
				? t(parcel ? 'driver.answerRefusedParcelPoint' : 'driver.answerRefusedPoint', {
						code: answer.code,
						reason,
						point: answer.pointCode,
					})
				: t('driver.answerRefusedDetail', { code: answer.code, reason })
			break
		}
	}
	return (
		<div
			role='status'
			className={cn('flex flex-col gap-2 rounded-lg border-2 p-4', TONE_CLASS[answer.tone])}
			data-testid='driver-answer'
			data-tone={answer.tone}
			data-outcome={answer.outcome}
		>
			<span className='w-fit rounded px-1.5 text-xs font-semibold uppercase ring-1 ring-current'>
				{t(`hubScanTone.${answer.tone}`)}
			</span>
			<div className='text-3xl font-extrabold leading-tight' data-testid='driver-answer-title'>
				{title}
			</div>
			<div className='text-base' data-testid='driver-answer-detail'>
				{detail}
			</div>
		</div>
	)
}
